import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, FileText, Circle, ExternalLink, Trash2, Loader2, X,
  Undo2, CalendarClock, Archive, Facebook, Instagram, Search,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LIBRARY } from "@/constants/testIds";
import { formatInTz, propertyTimezone } from "@/lib/timezone";
import { tr, uiLocale } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

function StatusPill({ status }) {
  if (status === "published_live") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] uppercase tracking-wider font-mono-display">
        <CheckCircle2 className="w-3 h-3" />{" "}{tr("Live")}</span>
    );
  }
  if (status === "scheduled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-200 bg-blue-50 text-blue-800 text-[10px] uppercase tracking-wider font-mono-display">
        <CalendarClock className="w-3 h-3" />{" "}{tr("Scheduled")}</span>
    );
  }
  if (status === "published_draft") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-200 bg-amber-50 text-amber-800 text-[10px] uppercase tracking-wider font-mono-display">
        <FileText className="w-3 h-3" />{" "}{tr("Draft in WP")}</span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--border)] bg-white text-[var(--ink-muted)] text-[10px] uppercase tracking-wider font-mono-display">
      <Circle className="w-3 h-3" />{" "}{tr("Not published")}</span>
  );
}

/**
 * Renders a Facebook / Instagram icon badge for every platform this
 * article has been published to. Colour matches the top-bar chips on
 * ArticlePage so editors get a consistent visual language across the
 * app (light blue = FB, light rose = IG).
 */
function SocialBadges({ article }) {
  const posts = article.social_posts || [];
  if (!posts.length) return null;
  const platforms = new Map();
  for (const p of posts) {
    if (!platforms.has(p.platform)) platforms.set(p.platform, p);
  }
  return (
    <>
      {[...platforms.values()].map((p) => {
        const isFb = p.platform === "facebook";
        return (
          <a
            key={p.platform}
            href={p.permalink || "#"}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={
              (isFb ? tr("Facebook post on ") : tr("Instagram post on ")) +
              (p.page_name || p.ig_username || "")
            }
            data-testid={`library-social-badge-${p.platform}-${article.id}`}
            className={
              "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono-display " +
              (isFb
                ? "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
                : "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100")
            }
          >
            {isFb ? (
              <Facebook className="w-3 h-3" />
            ) : (
              <Instagram className="w-3 h-3" />
            )}
          </a>
        );
      })}
    </>
  );
}

function daysUntilPurge(deletedAtIso) {
  if (!deletedAtIso) return null;
  const purgeAt = new Date(deletedAtIso).getTime() + 7 * 86400000;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / 86400000));
}

function effectiveStatus(a) {
  if (
    a.status === "scheduled" &&
    a.wordpress_scheduled_date &&
    new Date(a.wordpress_scheduled_date).getTime() <= Date.now()
  ) {
    return "published_live";
  }
  return a.status;
}

function ConfirmModal({ title, body, confirmLabel, confirmClass, busy, onCancel, onConfirm, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-modal`}
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white border border-[var(--border)] max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-serif-display text-lg">{title}</h3>
          <button onClick={onCancel} className="text-[var(--ink-muted)] hover:text-[var(--ink)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-sm text-[var(--ink-muted)] mb-5">{body}</div>
        <div className="flex items-center justify-end gap-2">
          <button
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-50"
          >{tr("Cancel")}</button>
          <button
            data-testid={`${testIdPrefix}-confirm`}
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white disabled:opacity-50 ${confirmClass}`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LibraryPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [statusFilter, setStatusFilter] = useState("all");
  // Instant search — filters the current tab's rows in-memory as the
  // editor types. No debounce: the list is already loaded (single
  // `/articles` fetch) and typical libraries stay well under 500
  // rows, so filtering is O(n) on every keystroke and stays well
  // under one frame at 60 fps. Cmd/Ctrl+K focuses the input from
  // anywhere on the page.
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingPurge, setPendingPurge] = useState(null);
  // Client-side pagination. Kept small (10 default) so the first
  // paint is snappy on modest laptops even with hundreds of rows in
  // the list — we still fetch everything so filter counts stay
  // accurate and jumps between pages don't hit the network.
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["articles"],
    queryFn: api.listArticles,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);
  const { data: trashed = [], isLoading: trashLoading } = useQuery({
    queryKey: ["articles-trash"],
    queryFn: api.listTrashedArticles,
    enabled: tab === "trash",
  });

  const softDelete = useMutation({
    mutationFn: (id) => api.deleteArticle(id),
    onSuccess: () => {
      toast.success(tr("Moved to trash — 7 days to restore"));
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["articles"] });
      qc.invalidateQueries({ queryKey: ["articles-trash"] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || tr("Could not delete"));
      setPendingDelete(null);
    },
  });

  const restore = useMutation({
    mutationFn: (id) => api.restoreArticle(id),
    onSuccess: () => {
      toast.success(tr("Article restored"));
      qc.invalidateQueries({ queryKey: ["articles"] });
      qc.invalidateQueries({ queryKey: ["articles-trash"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Restore failed")),
  });

  const purge = useMutation({
    mutationFn: (id) => api.purgeArticle(id),
    onSuccess: () => {
      toast.success(tr("Article permanently deleted"));
      setPendingPurge(null);
      qc.invalidateQueries({ queryKey: ["articles-trash"] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || tr("Purge failed"));
      setPendingPurge(null);
    },
  });

  const showArticles = tab === "articles";
  const articlesWithEffective = articles.map((a) => ({
    ...a,
    _effective_status: effectiveStatus(a),
  }));
  const counts = {
    all: articlesWithEffective.length,
    scheduled: articlesWithEffective.filter((a) => a._effective_status === "scheduled").length,
    published_live: articlesWithEffective.filter((a) => a._effective_status === "published_live").length,
    published_draft: articlesWithEffective.filter((a) => a._effective_status === "published_draft").length,
    unpublished: articlesWithEffective.filter(
      (a) =>
        a._effective_status !== "scheduled" &&
        a._effective_status !== "published_live" &&
        a._effective_status !== "published_draft"
    ).length,
  };
  const filteredArticles =
    statusFilter === "all"
      ? articlesWithEffective
      : statusFilter === "unpublished"
      ? articlesWithEffective.filter(
          (a) =>
            a._effective_status !== "scheduled" &&
            a._effective_status !== "published_live" &&
            a._effective_status !== "published_draft"
        )
      : articlesWithEffective.filter((a) => a._effective_status === statusFilter);

  // Second-pass filter: instant free-text search across the fields
  // an editor would actually look for a piece by — headline (primary),
  // dek/excerpt, writer, and slug for URL-hunting. Trash rows go
  // through the same pass so the search bar works there too. Empty
  // query = no-op passthrough. Memoised so we don't re-scan on every
  // parent render (e.g. mutation invalidations).
  const q = searchQuery.trim().toLowerCase();
  const searchedArticles = useMemo(() => {
    if (!q) return filteredArticles;
    return filteredArticles.filter((a) => {
      const hay = [
        a.headline, a.dek, a.excerpt, a.writer_name, a.slug,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [filteredArticles, q]);
  const searchedTrash = useMemo(() => {
    if (!q) return trashed;
    return trashed.filter((a) => {
      const hay = [
        a.headline, a.dek, a.excerpt, a.writer_name, a.slug,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [trashed, q]);
  const rows = showArticles ? searchedArticles : searchedTrash;
  const loading = showArticles ? isLoading : trashLoading;

  // Reset to page 1 whenever the visible set changes underneath us —
  // otherwise switching from "All (200)" to "Live (12)" while on
  // page 8 would show an empty page. Now also fires on search input
  // so paging + search stay coherent.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, tab, pageSize, q]);

  // Global keyboard shortcut: Cmd/Ctrl+K focuses the search input from
  // anywhere on the Library page. Escape clears the query and blurs
  // when the input already has focus.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      } else if (
        e.key === "Escape" &&
        document.activeElement === searchInputRef.current
      ) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const rangeStart = totalRows === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalRows);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const filterChips = [
    { key: "all", label: tr("All") },
    { key: "scheduled", label: tr("Scheduled"), icon: CalendarClock },
    { key: "published_live", label: tr("Live"), icon: CheckCircle2 },
    { key: "published_draft", label: tr("Draft in WP"), icon: FileText },
    { key: "unpublished", label: tr("Not published"), icon: Circle },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white">
        <div className="px-4 sm:px-10 py-5 sm:py-6">
          <div className="overline">{tr("Newsroom · Archive")}</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">{tr("Article Library")}</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-2">{tr("Every piece the desk has drafted. Click a title to review, edit, or publish.")}</p>
          <div className="mt-4 flex items-center gap-1 border-b border-[var(--border)] -mx-4 sm:-mx-10 px-4 sm:px-10">
            <button
              data-testid="library-tab-articles"
              onClick={() => setTab("articles")}
              className={
                "px-4 py-2 text-sm border-b-2 -mb-px " +
                (showArticles
                  ? "border-[var(--accent)] text-[var(--ink)] font-medium"
                  : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]")
              }
            >{tr("Articles · {articleCount}", { articleCount: articles.length })}
            </button>
            <button
              data-testid="library-tab-trash"
              onClick={() => setTab("trash")}
              className={
                "px-4 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 " +
                (!showArticles
                  ? "border-[var(--accent)] text-[var(--ink)] font-medium"
                  : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]")
              }
            >
              <Archive className="w-3.5 h-3.5" />{tr("Trash{value}", { value: trashed.length > 0 && ` · ${trashed.length}` })}
            </button>
          </div>

          {/* Instant search — filters the current tab's list as the
              editor types. Placed above the filter chips so the two
              filtering surfaces stack naturally (text-search + status). */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] pointer-events-none" />
              <input
                ref={searchInputRef}
                data-testid="library-search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={tr("Search headlines, deks, writers…  (⌘K)")}
                className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] font-mono-display"
              />
              {searchQuery && (
                <button
                  type="button"
                  data-testid="library-search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  aria-label={tr("Clear search")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {q && (
              <span
                data-testid="library-search-count"
                className="text-[11px] font-mono-display uppercase tracking-wider text-[var(--ink-muted)]"
              >
                {tr("{rowCount} match{s}", { rowCount: rows.length, s: rows.length === 1 ? "" : tr("es") })}
              </span>
            )}
          </div>
          {showArticles && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {filterChips.map((chip) => {
                const Icon = chip.icon;
                const active = statusFilter === chip.key;
                const count = counts[chip.key];
                return (
                  <button
                    key={chip.key}
                    data-testid={`${LIBRARY.filterChip}-${chip.key}`}
                    onClick={() => setStatusFilter(chip.key)}
                    className={
                      "inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono-display uppercase tracking-wider border transition-colors " +
                      (active
                        ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                        : "border-[var(--border)] bg-white text-[var(--ink-muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]")
                    }
                  >
                    {Icon && <Icon className="w-3 h-3" />}
                    <span>{chip.label}</span>
                    <span
                      className={
                        "inline-flex items-center justify-center min-w-[1.25rem] px-1 text-[10px] " +
                        (active
                          ? "bg-white/20 text-white"
                          : "bg-[var(--secondary)] text-[var(--ink-muted)]")
                      }
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        {loading && (
          <div className="text-sm text-[var(--ink-muted)]">{tr("Loading…")}</div>
        )}
        {!loading && rows.length === 0 && showArticles && (
          <div className="bg-white border border-[var(--border)] py-16 text-center">
            {articles.length === 0 ? (
              <>
                <div className="font-serif-display text-2xl mb-2">{tr("No articles yet")}</div>
                <div className="text-sm text-[var(--ink-muted)]">{tr("Head to")}{" "}<Link to="/research" className="text-[var(--accent)] underline">{tr("Research")}</Link>{" "}{tr("to scout your first story.")}</div>
              </>
            ) : (
              <>
                <div className="font-serif-display text-2xl mb-2">{tr("Nothing here")}</div>
                <div className="text-sm text-[var(--ink-muted)]">
                  {q ? (
                    <>{tr("No articles match “{searchQuery}”.", { searchQuery })}{" "}
                      <button
                        data-testid="library-search-reset-clear"
                        onClick={() => setSearchQuery("")}
                        className="text-[var(--accent)] underline"
                      >{tr("Clear search")}</button>
                    </>
                  ) : (
                    <>{tr("No articles match this filter.")}{" "}
                      <button
                        onClick={() => setStatusFilter("all")}
                        className="text-[var(--accent)] underline"
                      >{tr("Show all")}</button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {!loading && rows.length === 0 && !showArticles && (
          <div className="bg-white border border-[var(--border)] py-12 text-center">
            <div className="text-sm text-[var(--ink-muted)]">{tr("Trash is empty. Deleted articles appear here for 7 days before auto-purge.")}</div>
          </div>
        )}
        {rows.length > 0 && (
          <div className="bg-white border border-[var(--border)]">
            {pagedRows.map((a) => {
              const daysLeft = a.deleted_at ? daysUntilPurge(a.deleted_at) : null;
              const eff = showArticles ? a._effective_status : a.status;
              return (
                <div
                  key={a.id}
                  data-testid={`${LIBRARY.articleRow}-${a.id}`}
                  role={showArticles ? "button" : undefined}
                  tabIndex={showArticles ? 0 : -1}
                  onClick={showArticles ? () => navigate(`/article/${a.id}`) : undefined}
                  onKeyDown={(e) => {
                    if (showArticles && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      navigate(`/article/${a.id}`);
                    }
                  }}
                  className={
                    "flex items-start gap-4 px-4 sm:px-5 py-4 border-b border-[var(--border)] last:border-b-0 " +
                    (showArticles ? "hover:bg-[var(--secondary)] cursor-pointer" : "")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="overline">{a.writer_name}</span>
                      <span
                        className="text-[10px] text-[var(--ink-muted)] font-mono-display"
                        title={tr("When the article was generated")}
                      >
                        {new Date(a.created_at).toLocaleDateString(uiLocale())}
                      </span>
                      <StatusPill status={eff} />
                      {a.wordpress_published_at && eff === "published_live" && (
                        <span
                          className="text-[10px] text-emerald-700 font-mono-display"
                          title={tr("When it went live on the website")}
                          data-testid={`library-live-at-${a.id}`}
                        >{tr("live {when}", { when: formatInTz(a.wordpress_published_at, tz, "compact") })}
                        </span>
                      )}
                      <SocialBadges article={a} />
                      {!showArticles && daysLeft !== null && (
                        <span className="text-[10px] uppercase tracking-wider text-red-700 font-mono-display">{tr("purges in {daysLeft}d", { daysLeft })}</span>
                      )}
                    </div>
                    <h3 className="font-serif-display text-base sm:text-lg leading-snug">
                      {a.headline}
                    </h3>
                    {a.dek && (
                      <p className="text-sm text-[var(--ink-muted)] italic mt-1 font-serif-display">
                        {a.dek}
                      </p>
                    )}
                    {a.wordpress_scheduled_date && eff === "scheduled" && (
                      <p className="text-xs text-blue-700 mt-1 font-mono-display">{tr("goes live {value}", { value: new Date(a.wordpress_scheduled_date).toLocaleString(uiLocale()) })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 mt-1">
                    {a.wordpress_link && (
                      <a
                        href={a.wordpress_link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={tr("Open in WordPress")}
                        className="text-[var(--ink-muted)] hover:text-[var(--ink)] p-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    {showArticles ? (
                      <button
                        data-testid={`library-delete-btn-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(a);
                        }}
                        title={tr("Move to trash")}
                        className="text-[var(--ink-muted)] hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <button
                          data-testid={`library-restore-btn-${a.id}`}
                          onClick={() => restore.mutate(a.id)}
                          disabled={restore.isPending}
                          title={tr("Restore this article")}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" />{tr("Restore")}</button>
                        <button
                          data-testid={`library-purge-btn-${a.id}`}
                          onClick={() => setPendingPurge(a)}
                          title={tr("Delete permanently now")}
                          className="text-[var(--ink-muted)] hover:text-red-600 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {rows.length > 0 && (
          <div
            data-testid="library-pagination"
            className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs font-mono-display text-[var(--ink-muted)]"
          >
            <div className="flex items-center gap-2">
              {trRich("<0>Showing</0><1>{rangeStart}–{rangeEnd}</1><2>of</2><3>{totalRows}</3><4>·</4>", { rangeStart, rangeEnd, totalRows }, [<span />, <span className="text-[var(--ink)]" />, <span />, <span className="text-[var(--ink)]" />, <span />])}
              <label className="flex items-center gap-1.5">
                {trRich("<0>per page</0>", null, [<span />])}
                <select
                  data-testid="library-page-size"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="bg-[var(--surface)] border border-[var(--border)] px-1.5 py-0.5 text-[var(--ink)] outline-none focus:border-[var(--ink)]"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="library-page-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
              >{tr("Prev")}</button>
              {trRich("<0>Page {safePage} / {totalPages}</0>", { safePage, totalPages }, [<span className="px-2 text-[var(--ink)]" />])}
              <button
                type="button"
                data-testid="library-page-next"
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage >= totalPages}
                className="px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed"
              >{tr("Next")}</button>
            </div>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmModal
          title={tr("Move article to trash?")}
          body={
            <>
              <p className="text-[var(--ink)] mb-2">
                <span className="font-medium">{pendingDelete.headline}</span>
              </p>
              <p>{trRich("It'll sit in Trash for <0>7 days</0>. You can restore it any time before then — after 7 days it's purged automatically. This does <1>not</1> touch anything already published to WordPress.", null, [<strong />, <strong />])}</p>
            </>
          }
          confirmLabel={tr("Move to trash")}
          confirmClass="bg-red-600 hover:bg-red-700"
          busy={softDelete.isPending}
          onCancel={() => !softDelete.isPending && setPendingDelete(null)}
          onConfirm={() => softDelete.mutate(pendingDelete.id)}
          testIdPrefix="library-delete"
        />
      )}

      {pendingPurge && (
        <ConfirmModal
          title={tr("Purge permanently?")}
          body={
            <>
              <p className="text-[var(--ink)] mb-2">
                <span className="font-medium">{pendingPurge.headline}</span>
              </p>
              <p>{trRich("This deletes the article <0>permanently</0>. It cannot be restored. Any uploaded image will be removed too. WordPress is untouched.", null, [<strong />])}</p>
            </>
          }
          confirmLabel={tr("Purge permanently")}
          confirmClass="bg-red-700 hover:bg-red-800"
          busy={purge.isPending}
          onCancel={() => !purge.isPending && setPendingPurge(null)}
          onConfirm={() => purge.mutate(pendingPurge.id)}
          testIdPrefix="library-purge"
        />
      )}
    </div>
  );
}
